import React, { useEffect, useMemo, useState } from 'react';
import {
  NavLink,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { api, downloadJson } from './api.js';
import ExplainableRun from './ExplainableRun.jsx';
import ProcessingLedger from './ProcessingLedger.jsx';
import { RetiredLink } from './CurrentPlatform.jsx';
import { LiveDashboard as CurrentDashboard, LivePipeline as CurrentPipeline, LiveResults as CurrentResults, LiveDetail as CurrentResultDetail, LiveLayerViewer as CurrentLayers, LiveMultiLawProof as CurrentProof, LiveCapabilitySurface, CurrentIntakeResultTabs } from './LiveRule.jsx';
import { CurrentRunDetail } from './WorkspaceContinuity.jsx';
import JsonReceipt from './JsonReceipt.jsx';
import { VisibilityModeProvider, VisibilityModeSelector } from './VisibilityMode.jsx';
import './explainable.css';

const layerNames = [
  ['sources', 'Sources'],
  ['blocks', 'Raw Blocks'],
  ['actors', 'Actors'],
  ['help', 'Help'],
  ['workflow', 'Workflow'],
  ['temporal', 'Deadlines'],
  ['accountability', 'Accountability'],
  ['overrides', 'Overrides'],
  ['definitions', 'Definitions'],
  ['coverage', 'Coverage + Validation'],
];

const attentionClasses = new Set([
  'stale_handoff_placeholder',
  'pending_handoff',
  'rejected_failure',
  'incomplete_admissible_receipt',
  'unclassified',
]);

function useRemote(loader, dependencies = []) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let active = true;
    setState({ loading: true, data: null, error: null });
    loader()
      .then((data) => active && setState({ loading: false, data, error: null }))
      .catch((error) => active && setState({ loading: false, data: null, error }));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  return state;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function compactHash(value) {
  if (!value) return '—';
  const text = String(value);
  return text.length > 18 ? `${text.slice(0, 10)}…${text.slice(-6)}` : text;
}

function StatusBadge({ value }) {
  const normalized = String(value || 'unknown').toLowerCase();
  const kind =
    normalized.includes('reject') ||
    normalized.includes('fail') ||
    normalized.includes('error') ||
    normalized.includes('incomplete receipt')
      ? 'bad'
      : normalized.includes('control') ||
          normalized.includes('stale') ||
          normalized.includes('pending') ||
          normalized.includes('progress') ||
          normalized.includes('legacy') ||
          normalized.includes('partial')
        ? 'warn'
        : normalized.includes('complete') ||
            normalized.includes('admissible') ||
            normalized.includes('pass') ||
            normalized.includes('operational')
          ? 'good'
          : 'neutral';
  return <span className={`badge badge-${kind}`}>{String(value || 'unknown')}</span>;
}

function Loading({ label = 'Retrieving Rosetta receipts' }) {
  return (
    <div className="loading-panel">
      <span className="pulse" />
      <span>{label}</span>
    </div>
  );
}

function ErrorPanel({ error }) {
  return (
    <div className="error-panel">
      <strong>{error?.message || 'Request failed'}</strong>
      {error?.payload?.receipt?.details ? <p>{error.payload.receipt.details}</p> : null}
    </div>
  );
}

function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <header className="page-header">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

function Metric({ label, value, note }) {
  return (
    <article className="metric-card">
      <span>{label}</span>
      <strong>{Number(value || 0).toLocaleString()}</strong>
      <small>{note}</small>
    </article>
  );
}

function RunCard({ run, onOpen }) {
  const document = run.source_document || {};
  const footer = run.failure_code
    ? `FAILURE ${run.failure_code}`
    : run.output_content_hash
      ? `OUTPUT ${compactHash(run.output_content_hash)}`
      : 'NO TERMINAL OUTPUT RECEIPT';
  return (
    <button className="run-card" onClick={() => onOpen(run.id)} type="button">
      <div className="run-card-topline">
        <span className="mono">RUN {run.id}</span>
        <StatusBadge value={run.lifecycle_label || run.admissibility_state || run.run_status} />
      </div>
      <h3>{document.document_name || `Source document ${run.source_document_id}`}</h3>
      <p className="mono">
        {document.document_type || 'unknown type'} · ID {document.document_identifier || 'unavailable'}
      </p>
      <div className="run-receipts">
        <span>{run.lifecycle_detail || run.engine_version || 'No lifecycle classification returned'}</span>
        <span>{run.engine_version || 'engine receipt not attached'}</span>
        <span>{formatDate(run.completed_at || run.created_at)}{run.age_hours != null ? ` · ${run.age_hours}h old` : ''}</span>
      </div>
      <div className="hash-line mono">{footer}</div>
    </button>
  );
}

function Intake() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [operatorToken, setOperatorToken] = useState(() => sessionStorage.getItem('rosetta_operator_token') || '');
  const [form, setForm] = useState({
    document_name: '',
    document_identifier: '',
    source_url: '',
    corpus_name: 'Rosetta Operator Intake',
    reference_date: new Date().toISOString().slice(0, 10),
  });
  const [status, setStatus] = useState({ loading: false, result: null, error: null });

  function updateField(event) {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!file) return;
    sessionStorage.setItem('rosetta_operator_token', operatorToken);
    const body = new FormData();
    body.append('file', file);
    Object.entries(form).forEach(([key, value]) => value && body.append(key, value));
    setStatus({ loading: true, result: null, error: null });
    try {
      const result = await api('/api/intake', {
        method: 'POST',
        body,
        headers: { 'x-rosetta-operator-token': operatorToken },
      });
      setStatus({ loading: false, result, error: null });
    } catch (error) {
      setStatus({ loading: false, result: null, error });
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="LAW INTAKE"
        title="Process a Law"
        description="Upload any source material through the server-owned write boundary. Exact bytes and extracted text are preserved first, then the currently selected deterministic runtime is invoked without a generation hardwire."
      />
      <div className="two-column">
        <form className="panel intake-form" onSubmit={submit}>
          <label
            className={`drop-zone ${file ? 'drop-zone-loaded' : ''} ${dragActive ? 'drop-zone-active' : ''}`}
            onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
            onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
            onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
            onDrop={(event) => {
              event.preventDefault();
              setDragActive(false);
              const dropped = event.dataTransfer?.files?.[0] || null;
              if (dropped) setFile(dropped);
            }}
          >
            <input
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              accept=".pdf,.docx,.xlsx,.txt,.csv,.json,.html,.htm,.xml,.md,.sql,.rtf,.png,.jpg,.jpeg,.bmp,.tif,.tiff"
            />
            <span className="drop-icon">⇧</span>
            <strong>{file ? file.name : 'Click to upload or drag and drop'}</strong>
            <small>PDF, DOCX, XLSX, HTML/XML, UTF-8 text/CSV/JSON/MD/SQL/RTF, and PNG/JPEG/BMP/TIFF OCR. Legacy XLS is explicitly unsupported and must be converted to XLSX. Maximum 25 MB.</small>
          </label>

          <div className="form-grid">
            <label>
              <span>Document name</span>
              <input name="document_name" value={form.document_name} onChange={updateField} placeholder="Official title" />
            </label>
            <label>
              <span>Document identifier</span>
              <input name="document_identifier" value={form.document_identifier} onChange={updateField} placeholder="Bill ID, statute citation, or source ID" />
            </label>
            <label className="wide">
              <span>Official source URL</span>
              <input name="source_url" value={form.source_url} onChange={updateField} placeholder="Recommended; upload receipt used when absent" />
            </label>
            <label>
              <span>Corpus</span>
              <input name="corpus_name" value={form.corpus_name} onChange={updateField} />
            </label>
            <label>
              <span>Reference date</span>
              <input type="date" name="reference_date" value={form.reference_date} onChange={updateField} />
            </label>
            <label className="wide">
              <span>Operator token</span>
              <input
                type="password"
                value={operatorToken}
                onChange={(event) => setOperatorToken(event.target.value)}
                autoComplete="off"
                placeholder="Required for canonical writes"
              />
            </label>
          </div>
          <div className="intake-boundary">
            No browser credential can write to Rosetta tables directly. This form calls the Rosetta server through the server-owned privilege boundary, preserves source identity first, then dispatches through the selected deterministic runtime target. No older engine is substituted.
          </div>
          <button className="button button-primary" disabled={!file || status.loading} type="submit">
            {status.loading ? 'Processing source…' : 'Create deterministic receipt'}
          </button>
          {status.error ? <ErrorPanel error={status.error} /> : null}
        </form>

        <aside className="panel receipt-panel">
          <span className="eyebrow">INTAKE RECEIPT</span>
          {!status.result ? (
            <div className="empty-state">
              <strong>No source processed in this session.</strong>
              <p>The original extracted text appears here only after the server returns a source-bound extraction receipt.</p>
            </div>
          ) : (
            <>
              <div className="receipt-summary">
                <StatusBadge value={status.result.extraction?.status || 'recorded'} />
                <h2>{status.result.source_document?.document_name}</h2>
                <dl>
                  <dt>Source</dt>
                  <dd className="mono">{status.result.source_receipt?.source_content_id || '—'}</dd>
                  <dt>Selected runtime</dt>
                  <dd>{status.result.target?.ruleset_version || '—'}</dd>
                  <dt>Source hash</dt>
                  <dd className="mono">{status.result.source_receipt?.source_content_hash}</dd>
                  <dt>Ruleset SHA</dt>
                  <dd className="mono">{status.result.target?.ruleset_sha256 || '—'}</dd>
                </dl>
              </div>
              <div className="page-actions">
                <button
                  className="button"
                  onClick={() => downloadJson(`rosetta-source-${status.result.source_receipt?.source_content_id || 'receipt'}.json`, status.result)}
                  type="button"
                >
                  Export JSON
                </button>
                {status.result.source_receipt?.source_content_id ? <button
                  className="button button-primary"
                  onClick={() => navigate('/review/' + encodeURIComponent(status.result.source_receipt.source_content_id))}
                  type="button"
                >
                  Inspect current law
                </button> : null}
              </div>
              <details className="source-preview">
                <summary>Original extracted text</summary>
                <pre>{status.result.original_text}</pre>
              </details>
            </>
          )}
        </aside>
      </div>
      {status.result?.source_receipt?.source_content_id ? (
        <CurrentIntakeResultTabs
          sourceContentId={status.result.source_receipt.source_content_id}
          originalText={status.result.original_text || ''}
        />
      ) : null}
    </>
  );
}

function cellText(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function DataTable({ rows }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const columns = useMemo(() => {
    const keys = [];
    safeRows.slice(0, 20).forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!keys.includes(key)) keys.push(key);
      });
    });
    return keys.slice(0, 10);
  }, [safeRows]);
  if (!safeRows.length) return <div className="empty-state">No canonical rows returned for this surface.</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead><tr>{columns.map((column) => <th key={column}>{column.replaceAll('_', ' ')}</th>)}</tr></thead>
        <tbody>
          {safeRows.map((row, index) => (
            <tr key={row.id || row.source_content_id || row.manifest_id || index}>
              {columns.map((column) => <td key={column} title={cellText(row[column])}>{cellText(row[column])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Boundary() {
  const state = useRemote(() => api('/api/capabilities'), []);
  if (state.loading) return <Loading />;
  if (state.error) return <ErrorPanel error={state.error} />;
  const data = state.data;
  return (
    <>
      <PageHeader
        eyebrow="OPERATING BOUNDARY"
        title="Recovered Capability Contract"
        description={data.platform_boundary}
      />
      <div className="two-column">
        <section className="panel">
          <span className="eyebrow">RESTORED NOW</span>
          <ul className="contract-list">{data.restored_surfaces.map((item) => <li key={item}>{item.replaceAll('_', ' ')}</li>)}</ul>
        </section>
        <section className="panel">
          <span className="eyebrow">EXPLICIT LEGACY GAPS</span>
          <ul className="contract-list gap-list">{data.explicit_legacy_gaps.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      </div>
      <section className="panel boundary-statement">
        <h2>S → {'{ HELP, WORKFLOW, ACCOUNTABILITY, OVERRIDES, DEFINITIONS }'}</h2>
        <p>Rosetta owns decomposition, source receipts, layer coverage, and extraction provenance. Atlas owns correlation. Prism owns deterministic verification. Civic Genome owns family identity, lineage, events, and momentum. Lighthouse receives and presents without overwriting upstream truth.</p>
        <p className="mono">RETIRED EXECUTION READS: {data.retired_execution_read_contract || 'unavailable'}</p>
      </section>
    </>
  );
}


function RedirectToCurrentLaws() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/review', { replace: true });
  }, [navigate]);
  return null;
}

function Layout() {
  const navigate = useNavigate();
  const capabilityLinks = [
    ['/routes', 'Accountability Routes'],
    ['/actors', 'Actor Directory'],
    ['/references', 'Reference Explorer'],
    ['/procedures', 'Procedure Explorer'],
    ['/deadlines', 'Deadline Tracker'],
    ['/appeals', 'Appeal Pathways'],
    ['/workflows', 'Workflow Board'],
    ['/programs', 'Help Programs'],
    ['/policies', 'Policy Explorer'],
    ['/terms', 'Term Glossary'],
    ['/overrides', 'Override Manager'],
  ];

  const [globalSearch, setGlobalSearch] = useState('');
  const workbenchLinks = [
    ['/', 'Dashboard'],
    ['/intake', 'Law Intake'],
    ['/review', 'Current Laws'],
    ['/pipeline', 'Pipeline'],
    ['/layers', 'Layer Viewer'],
    ['/proof', 'Multi-Law Proof'],
    ['/boundary', 'Boundary'],
  ];
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <NavLink className="brand" to="/">
          <span className="brand-mark">R</span>
          <span><strong>ROSETTA</strong><small>LAW DECOMPOSITION PLATFORM</small></span>
        </NavLink>
        <nav className="primary-nav">
          {workbenchLinks.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === '/'}>{label}</NavLink>
          ))}
        </nav>
        <nav className="secondary-nav" aria-label="Rosetta capabilities and current authority gaps">
          {capabilityLinks.map(([to, label]) => (
            <NavLink key={to} to={to}>{label}</NavLink>
          ))}
        </nav>
        <div className="sidebar-contract">
          <span className="live-dot" />
          <strong>DETERMINISTIC</strong>
          <small>selected runtime · no fallback</small>
        </div>
      </aside>
      <main className="main-content">
        <div className="topbar">
          <span className="mono">ROSETTA / SOURCE → STRUCTURE</span>
          <form className="global-search" onSubmit={(event) => {
            event.preventDefault();
            const value = globalSearch.trim();
            if (value) navigate('/review/search/' + encodeURIComponent(value));
          }}>
            <input
              aria-label="Search current Rosetta sources"
              value={globalSearch}
              onChange={(event) => setGlobalSearch(event.target.value)}
              placeholder="Search current laws"
            />
            <button type="submit">SEARCH</button>
          </form>
          <span className="mono">SAME TRUTH · FOUR VIEWS</span>
        </div>
        <div className="content-frame">
          <VisibilityModeSelector compact />
          <Routes>
            <Route path="/" element={<CurrentDashboard />} />
            <Route path="/corpus" element={<RedirectToCurrentLaws />} />
            <Route path="/v2540" element={<RedirectToCurrentLaws />} />
            <Route path="/v2540/:kind/:id" element={<RedirectToCurrentLaws />} />
            <Route path="/review" element={<CurrentResults />} />
            <Route path="/review/search/:searchQuery" element={<CurrentResults />} />
            <Route path="/review/:sourceId" element={<CurrentResultDetail />} />
            <Route path="/review/:sourceId/:attemptId" element={<CurrentResultDetail />} />
            <Route path="/intake" element={<Intake />} />
            <Route path="/pipeline" element={<CurrentPipeline />} />
            <Route path="/corpus-audit" element={<RedirectToCurrentLaws />} />
            <Route path="/corpus-audit/:attemptId" element={<RedirectToCurrentLaws />} />
            <Route path="/processing" element={<ProcessingLedger><CurrentPipeline /></ProcessingLedger>} />
            <Route path="/processing/panel" element={<ProcessingLedger />} />
            <Route path="/layers" element={<CurrentLayers />} />
            <Route path="/layers/source" element={<CurrentLayers />} />
            <Route path="/layers/source/:sourceId" element={<CurrentLayers />} />
            <Route path="/audit/layers" element={<RedirectToCurrentLaws />} />
            <Route path="/audit" element={<RedirectToCurrentLaws />} />
            <Route path="/compare/engines" element={<RedirectToCurrentLaws />} />
            <Route path="/proof" element={<CurrentProof />} />
            <Route path="/routes" element={<LiveCapabilitySurface kind="routes" />} />
            <Route path="/routes/:sourceId" element={<LiveCapabilitySurface kind="routes" />} />
            <Route path="/actors" element={<LiveCapabilitySurface kind="actors" />} />
            <Route path="/actors/:sourceId" element={<LiveCapabilitySurface kind="actors" />} />
            <Route path="/references" element={<LiveCapabilitySurface kind="references" />} />
            <Route path="/references/:sourceId" element={<LiveCapabilitySurface kind="references" />} />
            <Route path="/procedures" element={<LiveCapabilitySurface kind="procedures" />} />
            <Route path="/procedures/:sourceId" element={<LiveCapabilitySurface kind="procedures" />} />
            <Route path="/deadlines" element={<LiveCapabilitySurface kind="deadlines" />} />
            <Route path="/deadlines/:sourceId" element={<LiveCapabilitySurface kind="deadlines" />} />
            <Route path="/appeals" element={<LiveCapabilitySurface kind="appeals" />} />
            <Route path="/appeals/:sourceId" element={<LiveCapabilitySurface kind="appeals" />} />
            <Route path="/workflows" element={<LiveCapabilitySurface kind="workflows" />} />
            <Route path="/workflows/:sourceId" element={<LiveCapabilitySurface kind="workflows" />} />
            <Route path="/programs" element={<LiveCapabilitySurface kind="programs" />} />
            <Route path="/programs/:sourceId" element={<LiveCapabilitySurface kind="programs" />} />
            <Route path="/policies" element={<LiveCapabilitySurface kind="policies" />} />
            <Route path="/policies/:sourceId" element={<LiveCapabilitySurface kind="policies" />} />
            <Route path="/terms" element={<LiveCapabilitySurface kind="terms" />} />
            <Route path="/terms/:sourceId" element={<LiveCapabilitySurface kind="terms" />} />
            <Route path="/overrides" element={<LiveCapabilitySurface kind="overrides" />} />
            <Route path="/overrides/:sourceId" element={<LiveCapabilitySurface kind="overrides" />} />
            <Route path="/boundary" element={<Boundary />} />
            <Route path="/history" element={<RetiredLink />} />
            <Route path="/history/runs" element={<RetiredLink />} />
            <Route path="/history/layers" element={<RetiredLink />} />
            <Route path="/pipeline/:runId" element={<CurrentRunDetail />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return <VisibilityModeProvider><Layout /></VisibilityModeProvider>;
}

